import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect} from 'react';
import {
  Box,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip
} from '@mui/material';
import SentenceStats from './SentenceStats';
import { findByLabel } from './ConfigHolder';
import { getBackgroundColor } from './resultUtils';
import { FaBookOpen, FaEllipsisV, FaSort, FaWaveSquare } from 'react-icons/fa';
import { Sentence } from './models/Sentence';
import { sentenceFactory } from './models/sentenceFactory';
import { useAutoPlay } from './contexts/AutoPlayContext';
import { isRTL } from './generalUtils';
import { BasicStates } from './fsm/AutoPlayBaseFSM';
import SearchToggleInput from './components/SearchToggleInput';
import useDualSearchInputs from './hooks/useDualSearchInputs';

const getExplanationFlags = (explanations) => {
  const flags = {
    hasGrammarExplanation: false,
    hasPronunciationExplanation: false,
  };

  if (!Array.isArray(explanations)) {
    return flags;
  }

  explanations.forEach((exp) => {
    if (!exp?.explanation) {
      return;
    }

    const explanationType = String(exp.explanationType || '').toLowerCase();
    if (explanationType === 'grammar') {
      flags.hasGrammarExplanation = true;
    }

    if (explanationType === 'pronunciation' || explanationType === 'phonetics') {
      flags.hasPronunciationExplanation = true;
    }
  });

  return flags;
};

const ExplanationBadge = ({ active, icon, label }) => {
  if (!active) {
    return (
      <Box
        aria-hidden="true"
        component="span"
        sx={{
          display: 'inline-flex',
          height: 14,
          width: 16,
        }}
      />
    );
  }

  return (
    <Tooltip title={`${label} explanation available`}>
      <Box
        component="span"
        aria-label={`${label} explanation available`}
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
          display: 'inline-flex',
          fontSize: '0.72rem',
          height: 14,
          justifyContent: 'center',
          opacity: 0.65,
          width: 16,
          '& svg': {
            fontSize: '0.72rem',
          },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
};

// Memoized row component to avoid re-rendering unaffected rows
const ContentRow = React.memo(function ContentRow({
  sentence,
  onRowClick,
  onShowDetails,
  columnIsRtl,
  bgColor,
  border,
  innerRef,
  hasGrammarExplanation,
  hasPronunciationExplanation,
}) {
  return (
    <TableRow
      ref={innerRef}
      onClick={onRowClick}
      sx={{
        backgroundColor: bgColor,
        cursor: 'pointer',
        alignItems: 'stretch',
      }}
    >
      <TableCell
        sx={{
          padding: '8px',
          verticalAlign: 'middle',
          borderLeft: border,
          direction: columnIsRtl[0] ? 'rtl' : 'ltr',
          textAlign: columnIsRtl[0] ? 'right' : 'left',
          borderBottom: '3px solid #fff',
        }}
      >
  {sentence.sentence}
      </TableCell>
      <TableCell
        sx={{
          padding: '8px',
          verticalAlign: 'middle',
          borderBottom: '3px solid #fff',
          direction: columnIsRtl[1] ? 'rtl' : 'ltr',
          textAlign: columnIsRtl[1] ? 'right' : 'left',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, height: '100%' }}>
          <span>{sentence.translation}</span>
          <Box
            sx={{
              alignItems: 'center',
              display: 'inline-flex',
              flexDirection: 'column',
              flexShrink: 0,
              justifyContent: 'center',
              lineHeight: 1,
              ml: 1,
              width: 28,
            }}
          >
            <ExplanationBadge
              active={hasGrammarExplanation}
              icon={<FaBookOpen />}
              label="Grammar"
            />
            <IconButton
              size="small"
              tabIndex={0}
              aria-label="Show details"
              onClick={(e) => {
                e.stopPropagation();
                onShowDetails();
              }}
              sx={{ cursor: 'pointer', p: 0.25 }}
            >
              <FaEllipsisV color="gray" />
            </IconButton>
            <ExplanationBadge
              active={hasPronunciationExplanation}
              icon={<FaWaveSquare />}
              label="Phonetics"
            />
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
}, (prev, next) => {
  // Only re-render when these values change
  return prev.isSelected === next.isSelected
  && prev.sentence === next.sentence
    && prev.bgColor === next.bgColor
    && prev.border === next.border
    && prev.hasGrammarExplanation === next.hasGrammarExplanation
    && prev.hasPronunciationExplanation === next.hasPronunciationExplanation;
});
// end ContentRow

const ShowContent = ({ content, sentenceCallBack, autoPlayState, 
                        errorCallBack, allContentTags, contentIndex,
                        deleteConfirmed, deleteConfirmedCallBack,
                        timingMode = 'fixedDelay', fixedDelaySeconds = 1.0,
                        autoplayPlaybackRate = 1.0,
                      }) => {
  // store the selected value as the original phrase index (stable across filters)
  // Initialize to 0 when content has phrases, otherwise null
  const [selectedOriginalIndex, setSelectedOriginalIndex] = useState(() => {
    try {
      return (content && Array.isArray(content.phrases) && content.phrases.length > 0) ? 0 : null;
    } catch (e) {
      return null;
    }
  });

  
  const [sentenceForStats, setSentenceForStats] = useState(null);
  const [isSentenceStatsVisible, setIsSentenceStatsVisible] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const {
    debouncedSearchTerm,
    inputRefs,
    searchTerm,
    showSearchInput,
    toggleSearchInput,
    updateSearchTerm,
    clearSearchTerm,
  } = useDualSearchInputs();

  const rowIndexRef = useRef(selectedOriginalIndex);

  // Prefer precomputed content.sentences but ensure it stays aligned when phrases change
  const sentences = useMemo(() => {
    if (!content || !Array.isArray(content.phrases)) return [];

    // If loader attached sentences and length matches, reuse them
    if (Array.isArray(content.sentences) && content.sentences.length === content.phrases.length) {
      return content.sentences;
    }

    // Fallback: recompute from phrases
    return content.phrases.map((_, idx) =>
      sentenceFactory(content, idx, { dontUpdateContent: true })
    );
  }, [content?.sentences, content?.phrases]);
  const learnCode = findByLabel(content?.Learn);
  const baseCode = findByLabel(content?.Base);
  const learnShortCode = learnCode?.value?.split('-')[0] || '';
  const baseShortCode = baseCode?.value?.split('-')[0] || '';
  const contentIsNumbers = !!content?.content?.includes?.("numbers");
  const columnIsRtl = [isRTL(content?.Learn), isRTL(content?.Base)];
  const isRtlDict = useMemo(() => ({ Learn: columnIsRtl[0], Base: columnIsRtl[1] }), [columnIsRtl[0], columnIsRtl[1]]);
  


  const ctx = useAutoPlay();
  const rowRefs = useRef([]);
  const rowRefsByOriginal = useRef({}); // map originalIndex -> DOM node
  const containerRef = useRef(null);
  const filteredPosRef = useRef(null); // track current position in filtered view (filteredRowIndex)
  const filteredIndicesRef = useRef([]);

  // set selected original index
  const setRowIndex = useCallback((originalIdx) => {
    rowIndexRef.current = originalIdx;
    // <-- ensure filteredPosRef points to the currently selected row
    const pos = (typeof originalIdx === 'number')
      ? filteredIndicesRef.current.indexOf(originalIdx)
      : -1;
    filteredPosRef.current = pos >= 0 ? pos : null;
    setSelectedOriginalIndex(originalIdx);
  }, [setSelectedOriginalIndex]);

  const selectOriginalIndex = useCallback((originalIdx, {updateSentence = true, } = {}) => {
   
    if (originalIdx === null || originalIdx === undefined || originalIdx < 0) {
      setRowIndex(null);
      if (updateSentence) {
        sentenceCallBack(new Sentence());
      }
      return;
    }

 
    setRowIndex(originalIdx);


   
    if (updateSentence) {
      const newSentence = sentenceFactory(content, originalIdx, { dontUpdateContent: true });
      const previousFilteredOriginal =
        typeof filteredPosRef.current === 'number' && filteredPosRef.current > 0
          ? filteredIndicesRef.current[filteredPosRef.current - 1]
          : undefined;
      newSentence.previousFilteredOriginal = previousFilteredOriginal;
  // Mark that this sentence was selected from the content table
  sentenceCallBack(newSentence, 'contentTable');
    }
  }, [content, sentenceCallBack, setRowIndex]);
  
  
  const playSelectedRow = () => {
    if (rowIndexRef.current !== null) {
  const newSentence = sentenceFactory(content, rowIndexRef.current, { dontUpdateContent: true });
  newSentence.timingMode = timingMode;
  newSentence.fixedDelaySeconds = fixedDelaySeconds;
  newSentence.autoplayPlaybackRate = autoplayPlaybackRate;
  sentenceCallBack(newSentence, 'contentTable');
    }
  };



  const advanceSelectedRow = () => {
    if (rowIndexRef.current !== null) {
      // prefer O(1) stored filtered position, fall back to indexOf if not available
      const currentFiltered = (typeof filteredPosRef.current === 'number') ? filteredPosRef.current : filteredIndices.indexOf(rowIndexRef.current);
      // const currentFiltered = filteredIndices.indexOf(rowIndexRef.current);

      if (currentFiltered >= 0 ) {
        if (currentFiltered < filteredIndices.length - 1) {
          const nextOriginal = filteredIndices[currentFiltered + 1];
          selectOriginalIndex(nextOriginal, { updateSentence: false});
          return;
        } else {
        if (currentFiltered == filteredIndices.length - 1){
          // setRowIndex(filteredIndices[0])
          selectOriginalIndex(filteredIndices[0], { updateSentence: false});

          ctx.dispatch({ type: 'stop' });
          // add here scrolling to the top
          containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }
    }
  }

    ctx.dispatch({ type: 'stop' });
  };

  useEffect(() => {
    if (ctx.autoPlayState !== BasicStates.IDLE) {
    advanceSelectedRow();
  }
  }, [ctx.ui.contentAdvanceRowToken]);

  useEffect(() => {
  if (ctx.autoPlayState !== BasicStates.IDLE) {
    playSelectedRow();
  }
  }, [ctx.ui.contentPlayRowToken]);

  // Ensure selectedOriginalIndex is initialized to 0 when content loads
  useEffect(() => {
    if ((selectedOriginalIndex === null || 
        selectedOriginalIndex === undefined) && 
    sentences && 
    sentences.length > 0) {
      setRowIndex(0);
    }
  }, [sentences, selectedOriginalIndex]);

  // === Compute filteredIndices: array of indices of sentences that match filters & sorting ===
  const filteredIndices = useMemo(() => {
  if (!content || !sentences.length) return [];
     // Filter indices based on searchTerm
    const search = debouncedSearchTerm;
    let filtered = sentences
      .map((sentence, idx) => ({ sentence, idx }))
      .filter(({ sentence }) => {
        const search0 = (search[0] || '').toLowerCase();
        const search1 = (search[1] || '').toLowerCase();
        const col0 = sentence.sentence?.toLowerCase() || '';
        const col1 = sentence.translation?.toLowerCase() || '';
        const tags = sentence.tags || [];
        const tagsText = Array.isArray(tags) ? tags.join(' ').toLowerCase() : '';
        let matchCol0 = col0.includes(search0) || tagsText.includes(search0);
        // don't include explanations (grammar and phonetics) for now
        // if (!matchCol0 && Array.isArray(phraseObj.phraseVal?.explanations)) {
        //   matchCol0 = phraseObj.phraseVal.explanations.some(
        //     (exp) =>
        //       typeof exp.explanation === 'string' &&
        //       exp.explanation.toLowerCase().includes(search0)
        //   );
        // }
  const matchCol1 = col1.includes(search1) || tagsText.includes(search1);
        return matchCol0 && matchCol1;
      });

      // Sort filtered array if needed
    if (sortColumn !== null) {
      filtered.sort((a, b) => {
        let aVal = sortColumn === 0 ? a.sentence.sentence : a.sentence.translation;
        let bVal = sortColumn === 0 ? b.sentence.sentence : b.sentence.translation;
        let code = sortColumn === 0 ? learnShortCode : baseShortCode;
        if (!aVal) aVal = '';
        if (!bVal) bVal = '';
        aVal = aVal.toLowerCase().trim();
        bVal = bVal.toLowerCase().trim();
        if (contentIsNumbers) {
          aVal = aVal.split(', ')[0].replace(/\D/g, '');
          bVal = bVal.split(', ')[0].replace(/\D/g, '');
          let aval = parseInt(aVal, 10);
          let bval = parseInt(bVal, 10);
          if (isNaN(aval)) aval = 0;
          if (isNaN(bval)) bval = 0;
          if (aval < bval) return sortAsc ? -1 : 1;
          if (aval > bval) return sortAsc ? 1 : -1;
          return 0;
        }
        const cmp = aVal.localeCompare(bVal, code, { sensitivity: 'base' });
        return sortAsc ? cmp : -cmp;
      });
    }
    // Return array of indices only
    return filtered.map(({ idx }) => idx);
  }, [content, sentences, 
    debouncedSearchTerm, 
    sortColumn, sortAsc, 
    learnShortCode, 
    baseShortCode, 
    contentIsNumbers]);

  useEffect(() => {
    filteredIndicesRef.current = filteredIndices;
  }, [filteredIndices]);

  useLayoutEffect(() => {
    // Ensure refs array length matches rendered rows to avoid stale entries
    if (Array.isArray(filteredIndices)) {
      rowRefs.current.length = filteredIndices.length;
    } else {
      rowRefs.current = [];
    }
  if (filteredIndices.length === sentences.length) {

          // If the selection still exists after filters changed, ensure it's visible
    if (selectedOriginalIndex !== null && filteredIndices.includes(selectedOriginalIndex)) {
      // run asynchronously so DOM refs are attached
      console.log('filteredIndices: content restored');
      setTimeout(() => {
        try {
          scrollHelper();
        } catch (e) {
          // ignore
        }
      }, 0);
    }
    }

  }, [filteredIndices]);

  useEffect(() => {
    if (!deleteConfirmed) return;
    deleteConfirmedCallBack(false);
  }, [deleteConfirmed, deleteConfirmedCallBack]);

  useEffect(() => {
    if (typeof contentIndex !== 'number' || contentIndex < 0) return;
    if (!filteredIndices.includes(contentIndex)) return;

    setRowIndex(contentIndex);
    if (contentIndex === 0) {
      setTimeout(() => {
        containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      }, 0);
    }
  }, [contentIndex, filteredIndices, setRowIndex]);
  
  useEffect(() => {
    if (!filteredIndices.length) {
      if (selectedOriginalIndex !== null) {
        selectOriginalIndex(null);
      }
      return;
    }
  }, [filteredIndices, selectOriginalIndex, selectedOriginalIndex]);

  useEffect(() => {
    if (ctx.autoPlayState === BasicStates.IDLE) return;
    if (!filteredIndices.length) return;

    const currentOriginalIndex = rowIndexRef.current;
    if (typeof currentOriginalIndex === 'number' && filteredIndices.includes(currentOriginalIndex)) {
      return;
    }

    selectOriginalIndex(filteredIndices[0], { updateSentence: false });
  }, [ctx.autoPlayState, filteredIndices, selectOriginalIndex]);

  const scrollHelper = () => {

    const filteredIndexForSelection = (typeof filteredPosRef.current === 'number') ? filteredPosRef.current : (selectedOriginalIndex !== null ? filteredIndices.indexOf(selectedOriginalIndex) : -1);
    // Prefer stable lookup by original index (works even if rows moved and memo prevented ref update)
    const row = rowRefsByOriginal.current[selectedOriginalIndex] || (filteredIndexForSelection >= 0 ? rowRefs.current[filteredIndexForSelection] : null);
    if (containerRef.current && filteredIndexForSelection >= 0 && row) {
      const container = containerRef.current;
       const rowTop = row.offsetTop;
       const rowHeight = row.offsetHeight;
       const containerHeight = container.clientHeight;
       console.log(`Row Top: ${rowTop}, Row Height: ${rowHeight}, Container Height: ${containerHeight}`);
 
       // Center row manually inside the container
       container.scrollTo({
         top: rowTop - containerHeight / 2 + rowHeight / 2,
         behavior: "smooth",
       });
     }
   }

  useLayoutEffect(() => {
    // rowIndexRef.current = selectedOriginalIndex;
    console.log(`Selected original index: ${selectedOriginalIndex}`);
    if (autoPlayState !== 'idle') {
      scrollHelper();
    }
  }, [selectedOriginalIndex]);





  if (!content || Object.keys(content).length === 0) {
    return <Typography variant="body1">This content is not yet available, stay tuned!</Typography>;
  }

  const keyLearn = `${learnCode.flag}`;
  const keyBase = `${baseCode.flag}`;
  const keys = [keyLearn, keyBase];

  if (contentIsNumbers) {
    keys[0] = `${keyLearn}`;
    keys[1] = "";
  }

  const getBorder = (filteredRowIndex) => {
    const originalIndex = filteredIndices[filteredRowIndex];
    return selectedOriginalIndex === originalIndex ? '4px solid #1976d2' : '4px solid transparent';
  };

  const disableContainer = (autoPlayState) => ({
    // height: '80vh',
    // background: '#f5f5f5',
    // width: '100%',
    // maxWidth: '100vw',
    // overflowX: 'auto',
    opacity: (autoPlayState !== 'idle') ? 0.5 : 1,
    pointerEvents: (autoPlayState !== 'idle') ? 'none' : 'auto',
    transition: 'opacity 0.2s',
  });


  // === Render ===
  return (
    <Box
      sx={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,                // <-- allow child to shrink
      overflowY: "hidden",        // parent should not scroll
      WebkitOverflowScrolling: 'touch',
    }}
    >
      <SentenceStats
        sentence={sentenceForStats}
        isOpen={isSentenceStatsVisible}
        isRtlDict = {isRtlDict}
        sentenceCallBack = {sentenceCallBack}
        onClose={() => setIsSentenceStatsVisible(false)}
        errorCallBack = {errorCallBack}
        allContentTags = {allContentTags}
      />
      <TableContainer
        ref={containerRef}
        component={Paper}
        sx={{
          flex: 1,
          minHeight: 0,              // <-- keep this
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          background: '#f5f5f5',
          width: '100%',
          maxWidth: '100vw',
          maxHeight: 'calc(100dvh - 220px)',   // <-- ensure a scrollable height for stickyHeader
          // maxHeight: 'calc(100vh - 290px)',
          ...disableContainer(autoPlayState),
        }}
      >
        <Table 
          stickyHeader 
          aria-label="content table" 
          sx={{ width: '100%', tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: '0px 0px' }}>
          <TableHead>
            <TableRow>
              {keys.map((key, index) => (
                <TableCell
                  key={index}
                  sx={{
                    minWidth: 120,
                    fontSize: '18px',
                    padding: '8px',
                    ...(index === keys.length - 1 ? { paddingRight: '24px' } : {})
                  }}
                >
                  <SearchToggleInput
                    active={!!(searchTerm[index] || showSearchInput[index])}
                    clearAriaLabel={`Clear search ${index}`}
                    iconButtonSx={{ p: 1, mx: 1 }}
                    inputRef={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    inputStyle={{
                      direction: columnIsRtl[index] ? 'rtl' : 'ltr',
                      textAlign: columnIsRtl[index] ? 'right' : 'left',
                    }}
                    onChange={(value) => updateSearchTerm(index, value)}
                    onClear={() => clearSearchTerm(index)}
                    onToggle={(e) => {
                      e.stopPropagation();
                      toggleSearchInput(index);
                    }}
                    showInput={showSearchInput[index]}
                    toolbarSx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontSize: '24px',
                      justifyContent: columnIsRtl[index] ? 'flex-start' : 'flex-end',
                      direction: columnIsRtl[index] ? 'rtl' : 'ltr',
                      textAlign: columnIsRtl[index] ? 'right' : 'left',
                      width: '100%',
                    }}
                    trailingContent={(
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSortColumn(index);
                          setSortAsc(sortColumn === index ? !sortAsc : true);
                        }}
                        sx={{ p: 1, mx: 1 }}
                      >
                        <FaSort color={sortColumn === index ? 'blue' : 'gray'} />
                      </IconButton>
                    )}
                    value={searchTerm[index]}
                    wrapperStyle={{ width: '85%' }}
                  >
                    <Box sx={{ flexGrow: 1, textAlign: 'left' }}>{key}</Box>
                  </SearchToggleInput>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredIndices.map((originalIndex, filteredRowIndex) => {
              const sentence = sentences[originalIndex];
              const bgColor = getBackgroundColor(sentence);
              const border = getBorder(filteredRowIndex);
              const {
                hasGrammarExplanation,
                hasPronunciationExplanation,
              } = getExplanationFlags(sentence.phraseVal?.explanations);

              const handleRowClick = () => {
                selectOriginalIndex(originalIndex);
              };

              const handleShowDetails = () => {
                setIsSentenceStatsVisible(true);
                setSentenceForStats(sentenceFactory(content, originalIndex, { dontUpdateContent: true }));
              };

              // Use originalIndex as key to keep identity stable across sorts/filters
              return (
                <ContentRow
                  key={originalIndex}
                  sentence={sentence}
                  onRowClick={handleRowClick}
                  onShowDetails={handleShowDetails}
                  columnIsRtl={columnIsRtl}
                  bgColor={bgColor}
                  border={border}
                  hasGrammarExplanation={hasGrammarExplanation}
                  hasPronunciationExplanation={hasPronunciationExplanation}
                  innerRef={(el) => {
                    // keep the array by visual index (used elsewhere), and maintain a stable map by original index
                    rowRefs.current[filteredRowIndex] = el;
                    if (el) {
                      rowRefsByOriginal.current[originalIndex] = el;
                    } else {
                      delete rowRefsByOriginal.current[originalIndex];
                    }
                  }}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ShowContent;
